// filepath: apps/web/src/features/lists/kanban/hooks/useKanbanDragDrop.ts
//
// FR-UI-VIEW-1 AC-5 / AC-6 — HTML5 drag-drop event-handler factory
// for the Kanban view.
//
// The hook returns THREE callbacks (`onCardDragStart`,
// `onColumnDragOver`, `onColumnDrop`) and the parent wires them onto
// <KanbanCard draggable /> and <KanbanColumn> respectively. The
// callbacks implement a small HTML5 drag-drop state machine and
// delegate the network round-trip to `useKanbanMove`.
//
// DRAG-DROP WIRE FORMAT
//   The card sets dataTransfer with three keys (delimited by ɢ
//   because Kanban names are typed under JSON constraints and
//   angle brackets would clash with the angle-bracket path):
//
//     application/x-kanban-card  :  recordId ɢ fromValue ɢ toValue-to-be
//     text/plain                 :  human-readable fallback for
//                                   browsers that block the typed key
//
// The drop target reads `application/x-kanban-card` first; the
// `text/plain` slot is for screen-reader announcements only.
//
// FAILURE ROUTING
//   - 409 from useKanbanMove → `onConflict(error, input)` callback.
//   - 422 from useKanbanMove → `onValidation(error, input)` callback.
//   - any other 4xx/5xx → `onFetchFailure(error, input)` callback.
//
// ROLLBACK POLICY (AC-6)
//   The hook does NOT mutate the local cache; the parent owns
//   optimistic state (it can re-key the dropped card to the
//   pre-drag column on error). The hook only fires callbacks —
//   this keeps the hook dependency-light and the rollback policy
//   visible at the call site.

import { useCallback } from "react";
import {
  HttpConflictError,
  HttpValidationError,
  type FieldErrorMap,
} from "@/definitions/types";
import { useKanbanMove, type KanbanMoveInput } from "./useKanbanMove";

/**
 * The HTML5 drag-drop payload (mirrors KanbanMoveInput + a transport
 * for the from-value at drop time).
 */
export interface KanbanDragPayload extends KanbanMoveInput {
  /**
   * Source column value captured at drag-start. Captured so the
   * drop handler can compare with the target column and no-op when
   * the user drops on the original column (saves a no-op network
   * round-trip).
   */
  fromValue: string;
}

/**
 * Public callbacks. The parent supplies all three error paths so it
 * can drive its own error UI (conflict modal, ValidationError,
 * FetchError).
 */
export interface UseKanbanDragDropCallbacks {
  /** Called when a conflict (HTTP 409) is detected. */
  onConflict?: (error: HttpConflictError, input: KanbanMoveInput) => void;
  /** Called when a validation failure (HTTP 422) is detected. */
  onValidation?: (error: HttpValidationError, input: KanbanMoveInput) => void;
  /**
   * Called for any other failure (HTTP 4xx/5xx/network). The parent
   * typically routes to the FetchError primitive or the list
   * status-envelope.
   */
  onFetchFailure?: (error: unknown, input: KanbanMoveInput) => void;
}

export interface UseKanbanDragDropOptions extends UseKanbanDragDropCallbacks {
  /** Entity type — used to build the PUT URL. */
  entityType: string;
  /** Definition version driving the cache key. */
  viewConfigVersion: number;
  /**
   * The enum field the Kanban groups by (i.e.
   * `definition.view_config.kanban.group_by`). Captured at the
   * column level so each drag carries it forward.
   */
  groupByField: string;
  /** API base URL override. Defaults to `/api`. */
  apiBase?: string;
}

/**
 * MIME type for the drag-drop payload. Custom MIME so the drop
 * target can distinguish the typed payload from `text/plain` (which
 * is the universal fallback). The literal value is set in the
 * `constants` block so tests can grep for it.
 */
export const KANBAN_CARD_MIME = "application/x-kanban-card";

/**
 * Separator inside the typed payload. A character unlikely to
 * appear in field names (per the SQL-safe-id allowlist at
 * FR-ENT-1 entity identifier whitelist) and outside ISO strings.
 */
const PAYLOAD_SEP = "\u241F"; // ɟ — unit separator-like character

export interface KanbanDragDropResult {
  /** Wire onto <KanbanCard draggable onDragStart={onCardDragStart} />. */
  onCardDragStart: (
    event: React.DragEvent<HTMLElement>,
    payload: Omit<KanbanDragPayload, "toValue">,
  ) => void;
  /** Wire onto <KanbanColumn onDragOver={onColumnDragOver} />. */
  onColumnDragOver: (event: React.DragEvent<HTMLElement>) => void;
  /** Wire onto <KanbanColumn onDrop={onColumnDrop} />. */
  onColumnDrop: (event: React.DragEvent<HTMLElement>, toValue: string) => void;
}

function encodePayload(p: KanbanDragPayload): string {
  return [p.recordId, p.fromValue, p.groupByField, String(p.rowVersion)].join(
    PAYLOAD_SEP,
  );
}

function decodePayload(raw: string): Omit<KanbanDragPayload, "toValue"> | null {
  const parts = raw.split(PAYLOAD_SEP);
  if (parts.length !== 4) return null;
  const [recordId, fromValue, groupByField, rowVersionRaw] = parts;
  if (!recordId || !fromValue || !groupByField || !rowVersionRaw) return null;
  const rowVersion = Number.parseInt(rowVersionRaw, 10);
  if (!Number.isFinite(rowVersion) || rowVersion < 1) return null;
  return {
    recordId,
    fromValue,
    groupByField,
    rowVersion,
  };
}

/**
 * useKanbanDragDrop — produces the three drag-drop event handlers
 * for a single Kanban board.
 *
 * The hook holds NO state. The HTML5 drag-drop state lives in the
 * browser's dataTransfer object; the SPA does not maintain a
 * parallel "currently dragging" state.
 */
export function useKanbanDragDrop(
  opts: UseKanbanDragDropOptions,
): KanbanDragDropResult {
  const {
    entityType,
    viewConfigVersion,
    onConflict,
    onValidation,
    onFetchFailure,
    apiBase = "/api",
  } = opts;

  const move = useKanbanMove({
    entityType,
    viewConfigVersion,
    apiBase,
  });

  const onCardDragStart = useCallback(
    (
      event: React.DragEvent<HTMLElement>,
      payload: Omit<KanbanDragPayload, "toValue">,
    ) => {
      if (!event.dataTransfer) return;
      const full: KanbanDragPayload = { ...payload, toValue: "" };
      const encoded = encodePayload(full);
      event.dataTransfer.setData(KANBAN_CARD_MIME, encoded);
      // text/plain — visual fallback so non-Canvas-aware inspectors
      // (browser devtools, e2e screenshot tools) can see something
      // moved. Not used by the drop handler.
      event.dataTransfer.setData("text/plain", payload.recordId);
      event.dataTransfer.effectAllowed = "move";
    },
    [],
  );

  const onColumnDragOver = useCallback(
    (event: React.DragEvent<HTMLElement>) => {
      // Prevent default to opt into the drop target. Without this
      // the browser rejects the drop (`dropEffect = "none"`).
      event.preventDefault();
      if (event.dataTransfer) {
        event.dataTransfer.dropEffect = "move";
      }
    },
    [],
  );

  const onColumnDrop = useCallback(
    (event: React.DragEvent<HTMLElement>, toValue: string) => {
      event.preventDefault();
      if (!event.dataTransfer) return;

      // Read typed payload first, fall back to text/plain only if
      // the typed key is missing (some browsers strip unknown MIME
      // types on cross-frame drags — defensive).
      const typed = event.dataTransfer.getData(KANBAN_CARD_MIME);
      if (typed === "") return;
      const payload = decodePayload(typed);
      if (!payload) return;

      // No-op short-circuit — dropping on the source column doesn't
      // trigger a mutation. Saves a 4xx round-trip + the optimistic
      // state's column placement.
      if (payload.fromValue === toValue) {
        return;
      }

      const input: KanbanMoveInput = {
        recordId: payload.recordId,
        groupByField: payload.groupByField,
        toValue,
        rowVersion: payload.rowVersion,
      };

      move.mutate(input, {
        onError: (err) => {
          // Order matters: most-specific class first. HttpConflictError
          // and HttpValidationError both extend ApiError; their
          // instanceof checks must precede the ApiError catch-all.
          if (err instanceof HttpConflictError) {
            onConflict?.(err, input);
            return;
          }
          if (err instanceof HttpValidationError) {
            onValidation?.(err, input);
            return;
          }
          // FR-UI-VIEW-1 AC-6 — the parent owns the rollback. The
          // callback signature is `(error, input)` regardless of
          // typed class so the rollback handler stays one branch.
          onFetchFailure?.(err, input);
        },
      });
    },
    [move, onConflict, onValidation, onFetchFailure],
  );

  return {
    onCardDragStart,
    onColumnDragOver,
    onColumnDrop,
  };
}

// Re-export the move hook here too so consumers import the
// drag-drop surface as a single module (matches
// apps/web/src/features/lists/kanban barrel). `FieldErrorMap` and
// `HttpValidationError` are also re-exported for callers that want
// to inspect the typed error directly (no semantic change vs the
// `@/definitions/types` import).
export { useKanbanMove } from "./useKanbanMove";
export type { KanbanMoveInput } from "./useKanbanMove";
export type { FieldErrorMap };
export { HttpValidationError, HttpConflictError };
